import { hexToBytes, recoverMessageAddress } from 'viem'

import { Address } from '~/utils/address'

import type { Hex, SignableMessage } from 'viem'

export type EoaSignatureCheck = 'valid' | 'signed-as-text' | 'mismatch'

/**
 * Checks a wallet's personal_sign signature over a 32-byte hash against the
 * scheme the smart-vault contracts verify: EIP-191 over the raw bytes.
 *
 * Some wallets re-encode the hash as text before signing. Those signatures are
 * reported as `signed-as-text` so the UI can name the cause.
 */
export const checkEoaSignature = async ({
  hash,
  signature,
  expectedAddress,
}: {
  hash: Hex
  signature: Hex
  expectedAddress: string
}): Promise<EoaSignatureCheck> => {
  const isSignedOver = async (message: SignableMessage) =>
    Address.equal(
      await recoverMessageAddress({ message, signature }),
      expectedAddress,
    )

  try {
    if (await isSignedOver({ raw: hash })) return 'valid'

    // The Rainbow extension with a Ledger signs the hex string.
    if (await isSignedOver(hash)) return 'signed-as-text'

    // Ledger Live over WalletConnect signs the bytes decoded as UTF-8.
    const decodedHash = new TextDecoder().decode(hexToBytes(hash))
    if (await isSignedOver(decodedHash)) return 'signed-as-text'
  } catch {
    // Malformed signature: fall through to mismatch.
  }
  return 'mismatch'
}
